import React, { useState, useCallback } from 'react';
import { Upload, FileText, AlertTriangle, Database } from 'lucide-react';
import * as XLSX from 'xlsx';

const FileUploadComponent = ({ onFileUpload, importTargets }) => {
  const [selectedTarget, setSelectedTarget] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const validateFile = (file) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/csv',
      'application/json', // .json
      'text/json'
    ];

    const allowedExtensions = ['.xlsx', '.xls', '.csv', '.json'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      throw new Error('Please upload a CSV, Excel, or JSON file (.csv, .xlsx, .xls, .json)');
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      throw new Error('File size must be less than 50MB');
    }

    return true;
  };

  const parseFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target.result;
          let parsed = [];

          if (file.name.toLowerCase().endsWith('.json')) {
            // Parse JSON
            const jsonData = JSON.parse(data);

            // Handle different JSON structures
            if (Array.isArray(jsonData)) {
              // Array of objects - convert to header + rows format
              if (jsonData.length > 0 && typeof jsonData[0] === 'object') {
                const headers = Object.keys(jsonData[0]);
                const rows = jsonData.map(obj => headers.map(header => obj[header] || ''));
                parsed = [headers, ...rows];
              } else {
                throw new Error('JSON array must contain objects with consistent properties');
              }
            } else if (typeof jsonData === 'object' && jsonData !== null) {
              // Single object - treat as one row
              const headers = Object.keys(jsonData);
              const row = headers.map(header => jsonData[header] || '');
              parsed = [headers, row];
            } else {
              throw new Error('JSON must be an array of objects or a single object');
            }
          } else {
            // Parse CSV/Excel using XLSX
            let workbook;
            if (file.name.toLowerCase().endsWith('.csv')) {
              workbook = XLSX.read(data, { type: 'string' });
            } else {
              workbook = XLSX.read(data, { type: 'array' });
            }

            // Get the first worksheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convert to JSON
            parsed = XLSX.utils.sheet_to_json(worksheet, {
              header: 1, // Return arrays instead of objects
              raw: false // Return formatted strings
            });
          }

          // Filter out empty rows
          parsed = parsed.filter(row => row.some(cell => cell && cell.toString().trim() !== ''));

          if (parsed.length === 0) {
            throw new Error('The file appears to be empty or contains no valid data');
          }

          // Check if the first row contains headers
          const firstRow = parsed[0];
          if (!firstRow || firstRow.length === 0) {
            throw new Error('The file must contain column headers in the first row');
          }

          // Validate that we have headers (not all numbers or empty)
          const hasValidHeaders = firstRow.some(header =>
            header &&
            isNaN(header) &&
            header.toString().trim().length > 0
          );

          if (!hasValidHeaders) {
            throw new Error('The first row must contain valid column headers (not just numbers or empty cells)');
          }

          // Return the parsed data with headers and rows separated
          resolve({
            headers: firstRow.map(h => h ? h.toString().trim() : ''),
            rows: parsed.slice(1),
            totalRows: parsed.length - 1,
            columns: firstRow.length
          });

        } catch (err) {
          reject(new Error(`Error parsing file: ${err.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };

      if (file.name.toLowerCase().endsWith('.csv') || file.name.toLowerCase().endsWith('.json')) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  const handleFileSelect = useCallback(async (file) => {
    if (!selectedTarget) {
      setError('Please select an import target first');
      return;
    }

    try {
      setError(null);
      setIsProcessing(true);

      validateFile(file);
      const parsedData = await parseFile(file);

      if (parsedData.totalRows === 0) {
        throw new Error('The file must contain at least one data row (excluding headers)');
      }

      await onFileUpload(file, selectedTarget, parsedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedTarget, onFileUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  return (
    <div className="space-y-6">
      {/* Target Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-4">1. Select Import Target</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(importTargets).map(([key, target]) => (
            <div
              key={key}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                selectedTarget === key
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedTarget(key)}
            >
              <div className="flex items-center gap-3 mb-2">
                <Database className="text-blue-600" size={20} />
                <h4 className="font-medium">{target.name}</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">{target.description}</p>
              <div className="text-xs">
                <div className="mb-1">
                  <span className="font-medium text-red-600">Required:</span>{' '}
                  {target.requiredFields.join(', ')}
                </div>
                <div>
                  <span className="font-medium text-gray-600">Optional:</span>{' '}
                  {target.optionalFields.slice(0, 3).join(', ')}
                  {target.optionalFields.length > 3 && '...'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* File Upload */}
      <div>
        <h3 className="text-lg font-semibold mb-4">2. Upload File</h3>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-400 bg-blue-50'
              : selectedTarget
              ? 'border-gray-300 hover:border-gray-400'
              : 'border-gray-200 bg-gray-50'
          } ${!selectedTarget ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onClick={() => {
            if (selectedTarget) {
              document.getElementById('file-input').click();
            }
          }}
        >
          {isProcessing ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-gray-600">Processing file...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className={`h-12 w-12 mb-4 ${selectedTarget ? 'text-gray-400' : 'text-gray-300'}`} />
              <p className={`text-lg font-medium mb-2 ${selectedTarget ? 'text-gray-700' : 'text-gray-400'}`}>
                {dragActive ? 'Drop your file here' : 'Drop your file here or click to browse'}
              </p>
              <p className={`text-sm ${selectedTarget ? 'text-gray-500' : 'text-gray-400'}`}>
                Supports CSV, XLSX, XLS, and JSON files (max 50MB)
              </p>
              {!selectedTarget && (
                <p className="text-sm text-red-500 mt-2">
                  Please select an import target first
                </p>
              )}
            </div>
          )}
          <input
            id="file-input"
            type="file"
            accept=".csv,.xlsx,.xls,.json"
            onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
            className="hidden"
            disabled={!selectedTarget || isProcessing}
          />
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="text-red-600 mt-0.5" size={20} />
          <div>
            <h4 className="text-red-800 font-medium">Upload Error</h4>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Requirements Panel */}
      {selectedTarget && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-blue-800 font-medium mb-2 flex items-center gap-2">
            <FileText size={16} />
            File Requirements for {importTargets[selectedTarget].name}
          </h4>
          <div className="text-sm text-blue-700 space-y-1">
            <p>• CSV/Excel: First row must contain column headers</p>
            <p>• JSON: Must be an array of objects or single object</p>
            <p>• Required fields: {importTargets[selectedTarget].requiredFields.join(', ')}</p>
            <p>• Optional fields: {importTargets[selectedTarget].optionalFields.join(', ')}</p>
            <p>• Empty rows will be automatically skipped</p>
            <p>• Duplicate data will be detected and handled during import</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadComponent;